import React, { useState } from 'react';
import './APIDocumentation.css';
import '../styles/clean-dashboard.css';

const APIDocumentation = ({ user }) => {
  const [activeSection, setActiveSection] = useState('overview');
  const [selectedEndpoint, setSelectedEndpoint] = useState(null);

  const sections = [
    { id: 'overview', title: 'Overview', icon: '📖' },
    { id: 'authentication', title: 'Authentication', icon: '🔐' },
    { id: 'endpoints', title: 'API Endpoints', icon: '🔗' },
    { id: 'webhooks', title: 'Webhooks', icon: '🪝' },
    { id: 'examples', title: 'Examples', icon: '💡' },
    { id: 'errors', title: 'Error Codes', icon: '⚠️' }
  ];

  const apiEndpoints = [
    {
      category: 'Authentication',
      endpoints: [
        {
          method: 'POST',
          path: '/api/login',
          title: 'User Login',
          description: 'Authenticate user and get access token',
          parameters: [
            { name: 'username', type: 'string', required: true, description: 'User username' },
            { name: 'password', type: 'string', required: true, description: 'User password' }
          ],
          response: {
            success: true,
            token: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...",
            user: { id: 1, username: "admin", role: "super_admin" }
          }
        },
        {
          method: 'GET',
          path: '/api/me',
          title: 'Get Current User',
          description: 'Get current authenticated user information',
          headers: [
            { name: 'Authorization', value: 'Bearer {token}', required: true }
          ],
          response: {
            success: true,
            user: { id: 1, username: "admin", role: "super_admin" }
          }
        }
      ]
    },
    {
      category: 'Instance Management',
      endpoints: [
        {
          method: 'GET',
          path: '/api/instances',
          title: 'Get User Instances',
          description: 'Get all WhatsApp instances accessible by current user',
          headers: [
            { name: 'Authorization', value: 'Bearer {token}', required: true }
          ],
          response: {
            success: true,
            instances: [
              {
                id: 1,
                phone_number: "628123456789",
                display_name: "My WhatsApp",
                status: "connected",
                webhook_url: "https://example.com/webhook",
                api_key: "abc123...",
                created_at: "2025-08-03T10:00:00Z"
              }
            ]
          }
        },
        {
          method: 'POST',
          path: '/api/instances',
          title: 'Create Instance',
          description: 'Create a new WhatsApp instance',
          headers: [
            { name: 'Authorization', value: 'Bearer {token}', required: true }
          ],
          parameters: [
            { name: 'phone_number', type: 'string', required: true, description: 'Phone number for the instance' },
            { name: 'display_name', type: 'string', required: false, description: 'Display name for the instance' }
          ],
          response: {
            success: true,
            instance_id: 1,
            message: "Instance created successfully"
          }
        },
        {
          method: 'POST',
          path: '/api/instances/{id}/connect',
          title: 'Connect Instance',
          description: 'Connect WhatsApp instance and generate QR code',
          headers: [
            { name: 'Authorization', value: 'Bearer {token}', required: true }
          ],
          response: {
            success: true,
            qr_code: "data:image/png;base64,iVBOR...",
            message: "Instance connecting, scan QR code"
          }
        }
      ]
    },
    {
      category: 'Messaging',
      endpoints: [
        {
          method: 'POST',
          path: '/api/instances/{id}/send',
          title: 'Send Message',
          description: 'Send a text message through WhatsApp instance',
          headers: [
            { name: 'Authorization', value: 'Bearer {token}', required: true }
          ],
          parameters: [
            { name: 'to', type: 'string', required: true, description: 'Recipient phone number' },
            { name: 'message', type: 'string', required: true, description: 'Message content' }
          ],
          response: {
            success: true,
            message_id: "msg_123456",
            message: "Message sent successfully"
          }
        },
        {
          method: 'POST',
          path: '/api/instances/{id}/media',
          title: 'Send Media',
          description: 'Send media (image, video, document) through WhatsApp',
          headers: [
            { name: 'Authorization', value: 'Bearer {token}', required: true }
          ],
          parameters: [
            { name: 'to', type: 'string', required: true, description: 'Recipient phone number' },
            { name: 'media_type', type: 'string', required: true, description: 'Type: image, video, document' },
            { name: 'media_url', type: 'string', required: true, description: 'URL to media file' },
            { name: 'caption', type: 'string', required: false, description: 'Media caption' }
          ]
        }
      ]
    },
    {
      category: 'External API',
      endpoints: [
        {
          method: 'GET',
          path: '/send-message',
          title: 'External Send Message',
          description: 'Send message using API key (no authentication required)',
          parameters: [
            { name: 'api_key', type: 'string', required: true, description: 'Instance API key' },
            { name: 'number', type: 'string', required: true, description: 'Recipient phone number' },
            { name: 'message', type: 'string', required: true, description: 'Message content' }
          ],
          example: 'http://localhost:8080/send-message?api_key=abc123&number=628123456789&message=Hello%20World'
        },
        {
          method: 'GET',
          path: '/send-media',
          title: 'External Send Media',
          description: 'Send media using API key (no authentication required)',
          parameters: [
            { name: 'api_key', type: 'string', required: true, description: 'Instance API key' },
            { name: 'number', type: 'string', required: true, description: 'Recipient phone number' },
            { name: 'media_type', type: 'string', required: true, description: 'Type: image, video, document' },
            { name: 'url', type: 'string', required: true, description: 'Media URL' },
            { name: 'caption', type: 'string', required: false, description: 'Media caption' }
          ]
        }
      ]
    }
  ];

  const webhookInfo = {
    overview: {
      title: 'Webhook Overview',
      description: 'Webhooks allow your application to receive real-time notifications when events occur in your WhatsApp instances.',
      features: [
        'Real-time message notifications',
        'Delivery status updates',
        'Connection status changes',
        'Admin chat bypass system',
        'Automatic response handling'
      ]
    },
    setup: [
      {
        step: 1,
        title: 'Configure Webhook URL',
        description: 'Set your webhook URL in the instance settings',
        code: 'PUT /api/instances/{id}/webhook\n{\n  "webhook_url": "https://your-app.com/webhook"\n}'
      },
      {
        step: 2,
        title: 'Handle Webhook Events',
        description: 'Your endpoint should accept POST requests and return HTTP 200',
        code: 'POST https://your-app.com/webhook\nContent-Type: application/json\n\n{\n  "event_type": "message",\n  "instance_id": 1,\n  "data": {...}\n}'
      },
      {
        step: 3,
        title: 'Implement Auto-Reply',
        description: 'Return response data to enable auto-reply functionality',
        code: '// Your webhook response\n{\n  "text": "Thank you for your message!",\n  "message": "We will get back to you soon"\n}'
      }
    ],
    events: [
      {
        type: 'message',
        description: 'Incoming message received',
        payload: {
          event_type: 'message',
          instance_id: 1,
          data: {
            from: '628123456789@s.whatsapp.net',
            message: 'Hello!',
            timestamp: '2025-08-03T10:00:00Z',
            message_type: 'text'
          }
        }
      },
      {
        type: 'status',
        description: 'Connection status changed',
        payload: {
          event_type: 'status',
          instance_id: 1,
          data: {
            status: 'connected',
            timestamp: '2025-08-03T10:00:00Z'
          }
        }
      }
    ]
  };

  const renderSection = () => {
    switch (activeSection) {
      case 'overview':
        return (
          <div className="doc-section">
            <h2>WhatsApp Multi-Instance API</h2>
            <p className="section-description">
              Complete REST API for managing multiple WhatsApp instances with role-based access control,
              webhook integration, and real-time messaging capabilities.
            </p>

            <div className="features-grid">
              <div className="feature-card">
                <div className="feature-icon">🔐</div>
                <h3>Role-based Access</h3>
                <p>User, Admin, and Super Admin roles with different permission levels</p>
              </div>
              <div className="feature-card">
                <div className="feature-icon">📱</div>
                <h3>Multi-Instance</h3>
                <p>Manage multiple WhatsApp accounts from a single API</p>
              </div>
              <div className="feature-card">
                <div className="feature-icon">🪝</div>
                <h3>Webhooks</h3>
                <p>Real-time notifications and auto-reply functionality</p>
              </div>
              <div className="feature-card">
                <div className="feature-icon">⚡</div>
                <h3>External API</h3>
                <p>Simple API key authentication for external integrations</p>
              </div>
            </div>

            <div className="getting-started">
              <h3>Getting Started</h3>
              <ol>
                <li>Authenticate using <code>/api/login</code> to get access token</li>
                <li>Create a WhatsApp instance using <code>/api/instances</code></li>
                <li>Connect the instance and scan QR code</li>
                <li>Start sending messages and receiving webhooks</li>
              </ol>
            </div>
          </div>
        );

      case 'authentication':
        return (
          <div className="doc-section">
            <h2>Authentication</h2>
            <p className="section-description">
              The API uses JWT (JSON Web Tokens) for authentication. Include the token in the Authorization header for all requests.
            </p>

            <div className="auth-methods">
              <div className="auth-method">
                <h3>JWT Token Authentication</h3>
                <p>For web applications and secure environments:</p>
                <pre className="code-block">
{`Authorization: Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...`}
                </pre>
              </div>

              <div className="auth-method">
                <h3>API Key Authentication</h3>
                <p>For external integrations using instance API keys:</p>
                <pre className="code-block">
{`GET /send-message?api_key=your_instance_api_key&...`}
                </pre>
              </div>
            </div>

            <div className="role-permissions">
              <h3>Role Permissions</h3>
              <div className="role-grid">
                <div className="role-card">
                  <h4>👤 User</h4>
                  <ul>
                    <li>Access assigned instances only</li>
                    <li>Send messages and media</li>
                    <li>View assigned instance data</li>
                  </ul>
                </div>
                <div className="role-card">
                  <h4>🔧 Admin</h4>
                  <ul>
                    <li>All user permissions</li>
                    <li>Manage webhooks and API keys</li>
                    <li>Create and manage users</li>
                    <li>Assign instance access</li>
                  </ul>
                </div>
                <div className="role-card">
                  <h4>⚡ Super Admin</h4>
                  <ul>
                    <li>All admin permissions</li>
                    <li>Full system access</li>
                    <li>Delete instances</li>
                    <li>System configuration</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        );

      case 'endpoints':
        return (
          <div className="doc-section">
            <h2>API Endpoints</h2>
            <p className="section-description">
              Complete reference for all available API endpoints organized by category.
            </p>

            <div className="endpoints-container">
              {apiEndpoints.map((category, categoryIndex) => (
                <div key={categoryIndex} className="endpoint-category">
                  <h3 className="category-title">{category.category}</h3>
                  <div className="endpoints-list">
                    {category.endpoints.map((endpoint, endpointIndex) => (
                      <div 
                        key={endpointIndex} 
                        className={`endpoint-item ${selectedEndpoint === `${categoryIndex}-${endpointIndex}` ? 'active' : ''}`}
                        onClick={() => setSelectedEndpoint(selectedEndpoint === `${categoryIndex}-${endpointIndex}` ? null : `${categoryIndex}-${endpointIndex}`)}
                      >
                        <div className="endpoint-header">
                          <span className={`method ${endpoint.method.toLowerCase()}`}>{endpoint.method}</span>
                          <span className="path">{endpoint.path}</span>
                          <span className="title">{endpoint.title}</span>
                        </div>
                        
                        {selectedEndpoint === `${categoryIndex}-${endpointIndex}` && (
                          <div className="endpoint-details">
                            <p className="description">{endpoint.description}</p>
                            
                            {endpoint.headers && (
                              <div className="headers-section">
                                <h4>Headers</h4>
                                {endpoint.headers.map((header, headerIndex) => (
                                  <div key={headerIndex} className="parameter">
                                    <code>{header.name}: {header.value}</code>
                                    {header.required && <span className="required">required</span>}
                                  </div>
                                ))}
                              </div>
                            )}

                            {endpoint.parameters && (
                              <div className="parameters-section">
                                <h4>Parameters</h4>
                                {endpoint.parameters.map((param, paramIndex) => (
                                  <div key={paramIndex} className="parameter">
                                    <strong>{param.name}</strong>
                                    <span className="type">({param.type})</span>
                                    {param.required && <span className="required">required</span>}
                                    <p>{param.description}</p>
                                  </div>
                                ))}
                              </div>
                            )}

                            {endpoint.response && (
                              <div className="response-section">
                                <h4>Response</h4>
                                <pre className="code-block">
                                  {JSON.stringify(endpoint.response, null, 2)}
                                </pre>
                              </div>
                            )}

                            {endpoint.example && (
                              <div className="example-section">
                                <h4>Example</h4>
                                <pre className="code-block">{endpoint.example}</pre>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'webhooks':
        return (
          <div className="doc-section">
            <h2>Webhooks</h2>
            <p className="section-description">{webhookInfo.overview.description}</p>

            <div className="webhook-features">
              <h3>Features</h3>
              <ul>
                {webhookInfo.overview.features.map((feature, index) => (
                  <li key={index}>{feature}</li>
                ))}
              </ul>
            </div>

            <div className="webhook-setup">
              <h3>Setup Guide</h3>
              {webhookInfo.setup.map((step, index) => (
                <div key={index} className="setup-step">
                  <div className="step-number">{step.step}</div>
                  <div className="step-content">
                    <h4>{step.title}</h4>
                    <p>{step.description}</p>
                    <pre className="code-block">{step.code}</pre>
                  </div>
                </div>
              ))}
            </div>

            <div className="webhook-events">
              <h3>Webhook Events</h3>
              {webhookInfo.events.map((event, index) => (
                <div key={index} className="event-item">
                  <h4>{event.type}</h4>
                  <p>{event.description}</p>
                  <pre className="code-block">
                    {JSON.stringify(event.payload, null, 2)}
                  </pre>
                </div>
              ))}
            </div>

            <div className="auto-reply-section">
              <h3>Auto-Reply System</h3>
              <p>Return response data from your webhook to enable automatic replies:</p>
              <pre className="code-block">
{`// Webhook response for auto-reply
{
  "text": "Your primary response message",
  "message": "Alternative response (fallback)"
}

// Admin bypass: Send "chat_admin" to skip webhook`}
              </pre>
            </div>
          </div>
        );

      case 'examples':
        return (
          <div className="doc-section">
            <h2>Code Examples</h2>
            <p className="section-description">
              Practical examples for common use cases and integrations.
            </p>

            <div className="examples-grid">
              <div className="example-card">
                <h3>JavaScript/Node.js</h3>
                <pre className="code-block">
{`// Login and send message
const axios = require('axios');

async function sendWhatsAppMessage() {
  // 1. Login
  const loginResponse = await axios.post('http://localhost:8080/api/login', {
    username: 'your_username',
    password: 'your_password'
  });
  
  const token = loginResponse.data.token;
  
  // 2. Send message
  const response = await axios.post(
    'http://localhost:8080/api/instances/1/send',
    {
      to: '628123456789',
      message: 'Hello from API!'
    },
    {
      headers: {
        'Authorization': \`Bearer \${token}\`,
        'Content-Type': 'application/json'
      }
    }
  );
  
  console.log('Message sent:', response.data);
}`}
                </pre>
              </div>

              <div className="example-card">
                <h3>Python</h3>
                <pre className="code-block">
{`import requests

# Login and send message
def send_whatsapp_message():
    # 1. Login
    login_response = requests.post('http://localhost:8080/api/login', {
        'username': 'your_username',
        'password': 'your_password'
    })
    
    token = login_response.json()['token']
    
    # 2. Send message
    response = requests.post(
        'http://localhost:8080/api/instances/1/send',
        json={
            'to': '628123456789',
            'message': 'Hello from Python!'
        },
        headers={
            'Authorization': f'Bearer {token}',
            'Content-Type': 'application/json'
        }
    )
    
    print('Message sent:', response.json())`}
                </pre>
              </div>

              <div className="example-card">
                <h3>PHP</h3>
                <pre className="code-block">
{`<?php
// Send message using API key (no auth required)
function sendWhatsAppMessage($apiKey, $number, $message) {
    $url = 'http://localhost:8080/send-message?' . http_build_query([
        'api_key' => $apiKey,
        'number' => $number,
        'message' => $message
    ]);
    
    $response = file_get_contents($url);
    return json_decode($response, true);
}

// Usage
$result = sendWhatsAppMessage(
    'your_api_key',
    '628123456789',
    'Hello from PHP!'
);

echo json_encode($result);
?>`}
                </pre>
              </div>

              <div className="example-card">
                <h3>Webhook Handler (Node.js)</h3>
                <pre className="code-block">
{`const express = require('express');
const app = express();

app.use(express.json());

// Webhook endpoint
app.post('/webhook', (req, res) => {
  const { event_type, instance_id, data } = req.body;
  
  if (event_type === 'message') {
    console.log('New message:', data.message);
    console.log('From:', data.from);
    
    // Auto-reply logic
    if (data.message.toLowerCase().includes('hello')) {
      return res.json({
        text: 'Hello! How can I help you today?'
      });
    }
    
    // Default response
    return res.json({
      text: 'Thank you for your message!'
    });
  }
  
  res.status(200).send('OK');
});

app.listen(3000, () => {
  console.log('Webhook server running on port 3000');
});`}
                </pre>
              </div>
            </div>
          </div>
        );

      case 'errors':
        return (
          <div className="doc-section">
            <h2>Error Codes</h2>
            <p className="section-description">
              Common error codes and their meanings when using the API.
            </p>

            <div className="error-codes">
              <div className="error-category">
                <h3>Authentication Errors</h3>
                <div className="error-list">
                  <div className="error-item">
                    <code className="error-code">401</code>
                    <div className="error-details">
                      <strong>Unauthorized</strong>
                      <p>Invalid or missing authentication token</p>
                    </div>
                  </div>
                  <div className="error-item">
                    <code className="error-code">403</code>
                    <div className="error-details">
                      <strong>Forbidden</strong>
                      <p>Insufficient permissions for the requested action</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="error-category">
                <h3>Client Errors</h3>
                <div className="error-list">
                  <div className="error-item">
                    <code className="error-code">400</code>
                    <div className="error-details">
                      <strong>Bad Request</strong>
                      <p>Invalid request format or missing required parameters</p>
                    </div>
                  </div>
                  <div className="error-item">
                    <code className="error-code">404</code>
                    <div className="error-details">
                      <strong>Not Found</strong>
                      <p>Requested resource does not exist</p>
                    </div>
                  </div>
                  <div className="error-item">
                    <code className="error-code">429</code>
                    <div className="error-details">
                      <strong>Too Many Requests</strong>
                      <p>Rate limit exceeded</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="error-category">
                <h3>Server Errors</h3>
                <div className="error-list">
                  <div className="error-item">
                    <code className="error-code">500</code>
                    <div className="error-details">
                      <strong>Internal Server Error</strong>
                      <p>Unexpected server error occurred</p>
                    </div>
                  </div>
                  <div className="error-item">
                    <code className="error-code">503</code>
                    <div className="error-details">
                      <strong>Service Unavailable</strong>
                      <p>WhatsApp service temporarily unavailable</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="error-handling">
              <h3>Error Response Format</h3>
              <pre className="code-block">
{`{
  "success": false,
  "error": "Error message description",
  "code": "ERROR_CODE",
  "details": {
    "field": "Additional error details"
  }
}`}
              </pre>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="api-documentation">
      <div className="doc-header">
        <h1>API Documentation</h1>
        <p>Complete guide for WhatsApp Multi-Instance API integration</p>
      </div>

      <div className="doc-container">
        <div className="doc-sidebar">
          <nav className="doc-nav">
            {sections.map(section => (
              <button
                key={section.id}
                className={`nav-item ${activeSection === section.id ? 'active' : ''}`}
                onClick={() => setActiveSection(section.id)}
              >
                <span className="nav-icon">{section.icon}</span>
                <span className="nav-title">{section.title}</span>
              </button>
            ))}
          </nav>
        </div>

        <div className="doc-content">
          {renderSection()}
        </div>
      </div>
    </div>
  );
};

export default APIDocumentation;
